import { useCallback, useEffect } from 'react';
import {
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  useIsMobile,
} from '@design-system';

import {
  InviteClientTabs,
  InviteClientContext,
  useInviteClientTabs,
  useInviteClientProvider,
  useInviteClientTrigger,
  useInviteClientStore,
  EmailDeliverabilityWarningModal,
  ExistingClientModal,
  InviteLinkCreatedModal,
  PrefillAnswersSection,
} from '@/features/invite-client';
import { useFeatureFlags } from '@client/contexts/FeatureFlagsContext';
import { useIsEdjPilotAdvisor } from '@/shared/hooks/use-edj-pilot-advisor';
import { useIsNylPilotClientCreationRestricted } from '@/shared/hooks/use-nyl-pilot-advisor';

interface InviteClientDialogContentProps {
  title?: string;
  description?: string;
}

function InviteClientDialogContent({
  title = 'Invite a client',
  description = 'Send an invitation to help your clients plan for long-term care',
}: InviteClientDialogContentProps) {
  const isMobile = useIsMobile();
  const { isInviteClientOpen, clearInviteClientParams } = useInviteClientTrigger();
  const isClientCreationRestricted = useIsNylPilotClientCreationRestricted();
  const {
    showAdditionalSettings,
    showEmailWarningModal,
    showPrefillAnswers,
    setShowPrefillAnswers,
    isAgentEnteredIntakeConsented,
    setAgentEnteredIntakeId,
    setIsAgentEnteredIntakeConsented,
    existingClientMatch,
    inviteClientConfig,
    isConsumerSmsMfaAvailable,
  } = useInviteClientStore();

  const showStackedDialog =
    showAdditionalSettings || showEmailWarningModal || existingClientMatch !== null;

  // Four tabs do not fit the default width. The triggers can shrink now, so the dialog no longer
  // gets pushed past the viewport either way — but truncated tab labels are a poor consolation,
  // and a strip this full simply needs the extra room.
  const { allowedTabs } = useInviteClientTabs();
  const needsWiderTabStrip = allowedTabs.length >= 4;
  const needsWiderBulkMfaRows = isConsumerSmsMfaAvailable && inviteClientConfig.isBulkMode;
  const isDialogOpen = isInviteClientOpen && !isClientCreationRestricted;

  const closeInviteClientDialog = useCallback(
    (forceResetConsentRequest = false) => {
      if (forceResetConsentRequest || isAgentEnteredIntakeConsented) {
        setAgentEnteredIntakeId(null);
        setIsAgentEnteredIntakeConsented(false);
      }
      setShowPrefillAnswers(false);
      clearInviteClientParams();
    },
    [
      clearInviteClientParams,
      isAgentEnteredIntakeConsented,
      setAgentEnteredIntakeId,
      setIsAgentEnteredIntakeConsented,
      setShowPrefillAnswers,
    ],
  );

  useEffect(() => {
    if (isInviteClientOpen && isClientCreationRestricted) {
      closeInviteClientDialog(true);
    }
  }, [closeInviteClientDialog, isClientCreationRestricted, isInviteClientOpen]);

  return (
    <>
      {!isMobile ? (
        <Dialog
          open={isDialogOpen}
          onOpenChange={open => {
            if (!open) {
              closeInviteClientDialog();
            }
          }}
        >
          <DialogContent
            className={cn('max-h-[90vh] w-full transition-all duration-200', {
              'overflow-y-auto overflow-x-hidden sm:max-w-xl':
                !showPrefillAnswers && !needsWiderTabStrip && !needsWiderBulkMfaRows,
              'overflow-y-auto overflow-x-hidden sm:max-w-2xl':
                !showPrefillAnswers && !needsWiderTabStrip && needsWiderBulkMfaRows,
              'max-h-[calc(100vh-1rem)]! overflow-y-auto overflow-x-hidden sm:max-w-[50.5rem]':
                !showPrefillAnswers && needsWiderTabStrip,
              'max-h-144 min-h-144 -translate-y-[calc(50%+1rem)] scale-[0.94]': showStackedDialog,
              'flex flex-col overflow-hidden sm:max-w-3xl': showPrefillAnswers,
            })}
          >
            {showPrefillAnswers ? (
              <>
                <DialogHeader className="sr-only">
                  <DialogTitle>Pre-fill answers</DialogTitle>
                  <DialogDescription>{description}</DialogDescription>
                </DialogHeader>
                <PrefillAnswersSection />
              </>
            ) : (
              <>
                <DialogHeader>
                  <DialogTitle>{title}</DialogTitle>
                  <DialogDescription>{description}</DialogDescription>
                </DialogHeader>
                <InviteClientTabs />
              </>
            )}
          </DialogContent>
        </Dialog>
      ) : (
        <Drawer
          open={isDialogOpen}
          onOpenChange={open => {
            if (!open) {
              closeInviteClientDialog();
            }
          }}
        >
          <DrawerContent
            className={cn('max-h-[90vh]! transition-all! duration-200! px-4 pb-6', {
              'after:hidden! overflow-y-auto overflow-x-hidden': !showPrefillAnswers,
              'bottom-1! h-144 scale-[0.96]': showStackedDialog,
              'flex flex-col overflow-hidden': showPrefillAnswers,
            })}
          >
            {showPrefillAnswers ? (
              <>
                <DrawerHeader className="sr-only">
                  <DrawerTitle>Pre-fill answers</DrawerTitle>
                  <DrawerDescription>{description}</DrawerDescription>
                </DrawerHeader>
                <PrefillAnswersSection />
              </>
            ) : (
              <>
                <DrawerHeader className="text-left">
                  <DrawerTitle>{title}</DrawerTitle>
                  <DrawerDescription>{description}</DrawerDescription>
                </DrawerHeader>
                <InviteClientTabs />
              </>
            )}
          </DrawerContent>
        </Drawer>
      )}

      <ExistingClientModal />
      <EmailDeliverabilityWarningModal />
      <InviteLinkCreatedModal />
    </>
  );
}

export interface InviteClientDialogProps extends InviteClientDialogContentProps {
  onInviteSuccess?: () => void;
}

export function InviteClientDialog({
  title,
  description,
  onInviteSuccess,
}: InviteClientDialogProps) {
  const { flags } = useFeatureFlags();
  const { clearInviteClientParams } = useInviteClientTrigger();
  const storeValue = useInviteClientProvider({
    onInviteSuccess,
    onClose: clearInviteClientParams,
  });

  const isEdjPilot = useIsEdjPilotAdvisor();
  const hideEmailAndFillIntakeForm = flags?.hideInviteEmailAndFillIntakeForm;

  const dialogDescription =
    hideEmailAndFillIntakeForm || isEdjPilot
      ? 'Get a unique link that you can share with clients through text, social media, or in person.'
      : description;

  return (
    <InviteClientContext.Provider value={storeValue}>
      <InviteClientDialogContent
        title={title}
        description={dialogDescription}
      />
    </InviteClientContext.Provider>
  );
}
